import styles from "./Sidebar.module.css";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    SquarePen,
    CalendarClock,
    CheckSquare,
    PanelLeftClose,
    PanelLeftOpen,
    Search,
    Bot,
    FolderKanban,
    ChevronDown,
    ChevronRight,
    Folder,
    User,
    MoreHorizontal,
    Pencil,
    Trash2,
    MapPin,
    Users,
    Home,
    History,
    SlidersHorizontal,
    Sparkles,
    RefreshCw
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { configApi, updateProjectApi, updateProjectResponsibilityApi, getLocationMembersApi, setLocationMembersApi, Project } from "../../api";
import { HistoryProviderOptions } from "../../components/HistoryProviders";
import { HistoryPanel } from "../../components/HistoryPanel";
import { useLogin, getToken, useMsalAuth } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { UserProfileMenu } from "../UserProfileMenu";
import { useUploadedFiles } from "../../contexts/UploadedFilesContext";
import { useProjects } from "../../contexts/ProjectContext";
import { useLocations } from "../../contexts/LocationContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { Alert, Badge, Button, FormField, Input, Menu, Modal } from "../ui";
import appLogo from "../../assets/applogo.svg";
import { getCurrentUser } from "../../api/currentUser";
import { getProjectGroupIdentity } from "./projectGrouping";

type SidebarProps = {
    sidebarRef: React.RefObject<HTMLElement>;
    sidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
};

type ProjectLocationGroup = {
    key: string;
    locationId: string | null;
    locationName: string;
    projects: Project[];
};

export const Sidebar = ({ sidebarRef, sidebarOpen, setSidebarOpen }: SidebarProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const uploadedFilesManager = useUploadedFiles();
    const {
        projects,
        isLoading: projectsLoading,
        updateProject,
        deleteProject,
        companyProjects,
        companyProjectsLoading,
        loadProjects,
        loadCompanyProjects,
        currentProject,
        refreshCurrentProject,
        setCurrentProject
    } = useProjects();
    const { locations } = useLocations();
    const { company, members } = useCompany();
    const { isOrganizationAdmin } = useOrganization();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState(false);
    const [projectsExpanded, setProjectsExpanded] = useState(true);
    const [companyProjectsExpanded, setCompanyProjectsExpanded] = useState(true);
    const [chatHistoryExpanded, setChatHistoryExpanded] = useState(false);
    const [editingLocationKey, setEditingLocationKey] = useState<string | null>(null);
    const [editingLocationName, setEditingLocationName] = useState("");
    const [isSavingLocationRename, setIsSavingLocationRename] = useState(false);
    const [userCompanyRole, setUserCompanyRole] = useState<"admin" | "member" | null>(null);
    const [isRoleLoading, setIsRoleLoading] = useState(true);
    const [roleLoadError, setRoleLoadError] = useState(false);
    const [companyLocationExpanded, setCompanyLocationExpanded] = useState<Record<string, boolean>>({});
    const [projectLocationExpanded, setProjectLocationExpanded] = useState<Record<string, boolean>>({});

    // Modal states for project actions
    const [renameModal, setRenameModal] = useState<{ isOpen: boolean; project: Project | null; value: string }>({
        isOpen: false,
        project: null,
        value: ""
    });
    const [locationModal, setLocationModal] = useState<{ isOpen: boolean; project: Project | null; value: string }>({
        isOpen: false,
        project: null,
        value: ""
    });
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; project: Project | null }>({
        isOpen: false,
        project: null
    });
    const [accessModal, setAccessModal] = useState<{ isOpen: boolean; project: Project | null; selectedOids: string[] }>({
        isOpen: false,
        project: null,
        selectedOids: []
    });
    const [locationAccessModal, setLocationAccessModal] = useState<{
        isOpen: boolean;
        locationId: string | null;
        locationName: string;
        projectIds: string[];
        selectedUserIds: string[];
    }>({
        isOpen: false,
        locationId: null,
        locationName: "",
        projectIds: [],
        selectedUserIds: []
    });
    const [locationAccessLoading, setLocationAccessLoading] = useState(false);
    const [locationAccessError, setLocationAccessError] = useState<string | null>(null);
    const [locationAccessLoaded, setLocationAccessLoaded] = useState(false);
    const [accessError, setAccessError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isAdmin = userCompanyRole === "admin" || isOrganizationAdmin;

    const groupedCompanyProjects = useMemo(() => {
        const groups: Record<string, typeof companyProjects> = {};
        companyProjects.forEach(project => {
            const locationName = project.location_name || t("projects.unassignedLocation");
            if (!groups[locationName]) {
                groups[locationName] = [];
            }
            groups[locationName].push(project);
        });
        return groups;
    }, [companyProjects, t]);

    const groupedProjects = useMemo<ProjectLocationGroup[]>(() => {
        const groups = new Map<string, ProjectLocationGroup>();

        projects.forEach(project => {
            const { key, locationId, locationName } = getProjectGroupIdentity(project, t("projects.unassignedLocation"));
            const existing = groups.get(key);

            if (existing) {
                existing.projects.push(project);
                if (locationId && !existing.locationName && locationName) {
                    existing.locationName = locationName;
                }
                return;
            }

            groups.set(key, {
                key,
                locationId,
                locationName,
                projects: [project]
            });
        });

        return Array.from(groups.values()).sort((a, b) => a.locationName.localeCompare(b.locationName));
    }, [projects, t]);

    useEffect(() => {
        const match = location.pathname.match(/^\/app\/projects\/([^/]+)/);
        const activeProjectId = match?.[1];
        if (!activeProjectId) return;
        const activeGroup = groupedProjects.find(group => group.projects.some(project => project.id === activeProjectId));
        if (activeGroup) {
            setProjectsExpanded(true);
            setProjectLocationExpanded(current => ({ ...current, [activeGroup.key]: true }));
        }
    }, [groupedProjects, location.pathname]);

    const locationIdByName = useMemo(() => {
        const byName = new Map<string, string>();
        locations.forEach(location => {
            const normalizedName = (location.name || "").trim().toLowerCase();
            if (!normalizedName || byName.has(normalizedName)) {
                return;
            }
            byName.set(normalizedName, location.id);
        });
        return byName;
    }, [locations]);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();

    useEffect(() => {
        configApi().then(config => {
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
        });
    }, []);

    // Load user company role
    useEffect(() => {
        const loadUserCompanyRole = async () => {
            setIsRoleLoading(true);
            setRoleLoadError(false);
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await getCurrentUser({ idToken: token });

                if (response.ok) {
                    const role = response.user?.company_role;
                    setUserCompanyRole(role === "admin" || role === "member" ? role : null);
                } else {
                    setRoleLoadError(true);
                }
            } catch (err) {
                console.error("Failed to load user company role:", err);
                setRoleLoadError(true);
            } finally {
                setIsRoleLoading(false);
            }
        };

        if (client || !useMsalAuth) {
            loadUserCompanyRole();
        }
    }, [client]);

    const handleHomeNavigation = async (event: React.MouseEvent) => {
        event.preventDefault(); // Prevent default NavLink behavior

        // Delete files from backend and clear from UI when going home
        uploadedFilesManager.clearFiles();

        // If already on home page, dispatch a custom event to clear chat
        if (location.pathname === "/app") {
            window.dispatchEvent(new CustomEvent("clearChat"));
        }

        // Always navigate to ensure state reset
        navigate("/app");
    };

    const navItems = [
        { to: "/app", icon: <SquarePen size={18} className={styles.sidebarIcon} />, label: t("home"), isHome: true },
        { to: "/app/agent", icon: <Bot size={18} className={styles.sidebarIcon} />, label: t("agent"), isHome: false },
        { to: "/app/search", icon: <Search size={18} className={styles.sidebarIcon} />, label: t("search"), isHome: false },
        { to: "/app/workspace", icon: <CheckSquare size={18} className={styles.sidebarIcon} />, label: "Workspace", isHome: false },
        { to: "/app/meetings", icon: <CalendarClock size={18} className={styles.sidebarIcon} />, label: t("meetings"), isHome: false }
    ];
    if (isAdmin) {
        navItems.push({ to: "/app/insights", icon: <Sparkles size={18} className={styles.sidebarIcon} />, label: "AI Insights", isHome: false });
    }

    const handleProjectNavigation = (project: Project) => {
        const targetPath = `/app/projects/${project.id}`;

        if (location.pathname === targetPath) {
            window.dispatchEvent(new CustomEvent("clearProjectChat"));
            return;
        }

        setCurrentProject(project);
        navigate(targetPath);
    };

    // Toggle user selection for access modal
    const toggleAccessUser = (oid: string) => {
        setAccessModal(prev => ({
            ...prev,
            selectedOids: prev.selectedOids.includes(oid) ? prev.selectedOids.filter(id => id !== oid) : [...prev.selectedOids, oid]
        }));
    };

    // Save access changes
    const handleSaveAccess = async () => {
        if (!accessModal.project) return;

        setIsSubmitting(true);
        setAccessError(null);
        try {
            const token = client ? await getToken(client) : undefined;
            await updateProjectResponsibilityApi(accessModal.project.id, accessModal.selectedOids, token);
            // Refresh projects to get updated data
            await loadProjects();
            if (company) {
                await loadCompanyProjects();
            }
            setAccessError(null);
            setAccessModal({ isOpen: false, project: null, selectedOids: [] });
        } catch (error) {
            console.error("Failed to update access:", error);
            setAccessError(error instanceof Error ? error.message : t("projects.manageAccessError"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleLocationAccessUser = (userId: string) => {
        setLocationAccessModal(prev => ({
            ...prev,
            selectedUserIds: prev.selectedUserIds.includes(userId) ? prev.selectedUserIds.filter(id => id !== userId) : [...prev.selectedUserIds, userId]
        }));
    };

    const closeLocationAccessModal = () => {
        setLocationAccessError(null);
        setLocationAccessLoading(false);
        setLocationAccessLoaded(false);
        setLocationAccessModal({
            isOpen: false,
            locationId: null,
            locationName: "",
            projectIds: [],
            selectedUserIds: []
        });
    };

    const openLocationAccessModal = async (locationId: string | null, locationName: string, groupProjects: Project[]) => {
        setLocationAccessError(null);
        setLocationAccessLoading(true);
        setLocationAccessLoaded(false);
        setLocationAccessModal({
            isOpen: true,
            locationId,
            locationName,
            projectIds: groupProjects.map(project => project.id),
            selectedUserIds: []
        });

        try {
            if (locationId) {
                const token = client ? await getToken(client) : undefined;
                const locationMembers = await getLocationMembersApi(locationId, token);
                setLocationAccessModal(prev => ({
                    ...prev,
                    selectedUserIds: locationMembers.map(member => member.id)
                }));
            } else {
                const sharedUserIds =
                    groupProjects.length === 0
                        ? []
                        : groupProjects.reduce<string[]>((shared, project, index) => {
                              const currentIds = project.responsible_users?.map(user => user.oid) || [];
                              if (index === 0) {
                                  return [...new Set(currentIds)];
                              }
                              const currentSet = new Set(currentIds);
                              return shared.filter(userId => currentSet.has(userId));
                          }, []);
                setLocationAccessModal(prev => ({
                    ...prev,
                    selectedUserIds: sharedUserIds
                }));
            }
            setLocationAccessLoaded(true);
        } catch (error) {
            console.error("Failed to load location access members:", error);
            setLocationAccessError(error instanceof Error ? error.message : t("projects.manageLocationAccessError"));
        } finally {
            setLocationAccessLoading(false);
        }
    };

    const handleSaveLocationAccess = async () => {
        if (!locationAccessLoaded) return;

        setIsSubmitting(true);
        setLocationAccessError(null);
        try {
            const token = client ? await getToken(client) : undefined;
            if (locationAccessModal.locationId) {
                await setLocationMembersApi(locationAccessModal.locationId, locationAccessModal.selectedUserIds, token);
            } else {
                for (const projectId of locationAccessModal.projectIds) {
                    await updateProjectResponsibilityApi(projectId, locationAccessModal.selectedUserIds, token);
                }
            }
            await loadProjects();
            if (company) {
                await loadCompanyProjects();
            }
            if (currentProject) {
                await refreshCurrentProject(currentProject.id);
            }
            closeLocationAccessModal();
        } catch (error) {
            console.error("Failed to save location access:", error);
            setLocationAccessError(error instanceof Error ? error.message : t("projects.manageLocationAccessError"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRenameLocationGroup = async (locationName: string, groupProjects: Project[]) => {
        const trimmedName = editingLocationName.trim();
        if (!trimmedName) {
            alert("Location name is required");
            return;
        }

        if (trimmedName === locationName) {
            setEditingLocationKey(null);
            setEditingLocationName("");
            return;
        }

        try {
            setIsSavingLocationRename(true);
            const token = client ? await getToken(client) : undefined;
            for (const project of groupProjects) {
                await updateProjectApi(project.id, { location_id: null, location_name: trimmedName }, token);
            }
            await loadProjects();
            if (company) {
                await loadCompanyProjects();
            }
            if (currentProject && groupProjects.some(project => project.id === currentProject.id)) {
                await refreshCurrentProject(currentProject.id);
            }
            setEditingLocationKey(null);
            setEditingLocationName("");
        } catch (error) {
            console.error("Failed to rename location group:", error);
            alert("Failed to rename location");
        } finally {
            setIsSavingLocationRename(false);
        }
    };

    const startRenameLocationGroup = (locationKey: string, locationName: string, groupProjects: Project[], locationId: string | null) => {
        if (locationId) {
            alert("This location is managed in Settings. Rename it there.");
            return;
        }
        setEditingLocationKey(locationKey);
        setEditingLocationName(locationName);
    };

    const cancelRenameLocationGroup = () => {
        setEditingLocationKey(null);
        setEditingLocationName("");
    };

    return (
        <nav
            ref={sidebarRef}
            className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarActive : ""} ${!sidebarOpen ? styles.sidebarCollapsed : ""} glass-blur-fallback`}
            aria-label="Sidebar Navigation"
        >
            <div className={`${styles.sidebarStickyHeader} ${!sidebarOpen ? styles.sidebarStickyHeaderCollapsed : ""}`}>
                <div className={`${styles.sidebarTopBar} ${!sidebarOpen ? styles.sidebarTopBarCollapsed : ""}`}>
                    {sidebarOpen && (
                        <div className={styles.sidebarBrand} onClick={handleHomeNavigation} style={{ cursor: "pointer" }}>
                            <img src={appLogo} alt="Company logo" width="20" height="20" className={styles.sidebarLogo} />
                            <span className={styles.sidebarCompanyName}>Erhol Care</span>
                        </div>
                    )}

                    <div
                        className={`${styles.sidebarTopIcon}`}
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                    >
                        {sidebarOpen ? <PanelLeftClose size={24} /> : <PanelLeftOpen size={24} />}
                    </div>
                </div>

                <ul>
                    {navItems.map(({ to, icon, label, isHome }) => (
                        <li key={to}>
                            {isHome ? (
                                <div
                                    className={location.pathname === "/app" ? styles.sidebarNavLinkActive : styles.sidebarNavLink}
                                    onClick={handleHomeNavigation}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className={styles.navContent}>
                                        {icon}
                                        {sidebarOpen && <span>{label}</span>}
                                    </div>
                                </div>
                            ) : (
                                <NavLink
                                    to={to}
                                    className={({ isActive }) => (isActive ? styles.sidebarNavLinkActive : styles.sidebarNavLink)}
                                    onClick={async () => {
                                        // Clear files when navigating to different sections
                                        uploadedFilesManager.clearFiles();
                                    }}
                                >
                                    <div className={styles.navContent}>
                                        {icon}
                                        {sidebarOpen && <span>{label}</span>}
                                    </div>
                                </NavLink>
                            )}
                        </li>
                    ))}

                    {/* My Projects Section */}
                    {sidebarOpen && (
                        <li className={styles.projectsSection}>
                            <div className={styles.projectsHeader} onClick={() => setProjectsExpanded(!projectsExpanded)}>
                                <div className={styles.projectsHeaderLeft}>
                                    <FolderKanban size={18} className={styles.sidebarIcon} />
                                    <span>{t("projects.myProjects")}</span>
                                </div>
                                <div className={styles.projectsHeaderRight}>
                                    <button
                                        type="button"
                                        className={styles.projectsRefreshButton}
                                        aria-label={t("projects.refresh", "Refresh projects")}
                                        title={t("projects.refresh", "Refresh projects")}
                                        disabled={projectsLoading}
                                        onClick={event => {
                                            event.stopPropagation();
                                            void loadProjects();
                                        }}
                                    >
                                        <RefreshCw className={projectsLoading ? styles.refreshSpinning : ""} size={14} />
                                    </button>
                                    {projectsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </div>
                            </div>

                            {projectsExpanded && (
                                <ul className={styles.projectsList}>
                                    {projects.length === 0 ? (
                                        <li className={styles.emptyProjects}>{t("projects.noProjects")}</li>
                                    ) : (
                                        groupedProjects.map(group => {
                                            const locationName = group.locationName;
                                            const resolvedLocationId = group.locationId || locationIdByName.get(locationName.trim().toLowerCase()) || null;
                                            const groupProjects = group.projects;
                                            const isExpanded = projectLocationExpanded[group.key] ?? true;
                                            const locationMenuKey = `projects:${group.key}`;
                                            return (
                                                <li key={group.key} className={styles.locationGroup}>
                                                    <div
                                                        className={styles.locationHeader}
                                                        onClick={() => {
                                                            if (editingLocationKey === locationMenuKey) {
                                                                return;
                                                            }
                                                            setProjectLocationExpanded(prev => ({
                                                                ...prev,
                                                                [group.key]: !isExpanded
                                                            }));
                                                        }}
                                                    >
                                                        {editingLocationKey === locationMenuKey ? (
                                                            <input
                                                                className={styles.locationRenameInput}
                                                                value={editingLocationName}
                                                                onChange={e => setEditingLocationName(e.target.value)}
                                                                onClick={e => e.stopPropagation()}
                                                                disabled={isSavingLocationRename}
                                                            />
                                                        ) : (
                                                            <div className={styles.locationTitleWrapper}>
                                                                <Home size={14} className={styles.locationIcon} />
                                                                <span className={styles.locationTitle}>{locationName}</span>
                                                            </div>
                                                        )}
                                                        <div className={styles.locationHeaderActions}>
                                                            {editingLocationKey === locationMenuKey ? (
                                                                <div className={styles.locationRenameActions}>
                                                                    <Button
                                                                        variant="primary"
                                                                        size="xs"
                                                                        onClick={async e => {
                                                                            e.stopPropagation();
                                                                            await handleRenameLocationGroup(locationName, groupProjects);
                                                                        }}
                                                                        disabled={isSavingLocationRename}
                                                                    >
                                                                        {t("projectPage.save")}
                                                                    </Button>
                                                                    <Button
                                                                        variant="secondary"
                                                                        size="xs"
                                                                        onClick={e => {
                                                                            e.stopPropagation();
                                                                            cancelRenameLocationGroup();
                                                                        }}
                                                                        disabled={isSavingLocationRename}
                                                                    >
                                                                        {t("projectPage.cancel")}
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <Menu>
                                                                    <Menu.Trigger asChild>
                                                                        <button type="button" className={styles.menuTrigger} onClick={e => e.stopPropagation()}>
                                                                            <MoreHorizontal size={14} />
                                                                        </button>
                                                                    </Menu.Trigger>
                                                                    <Menu.Content>
                                                                        <Menu.Item
                                                                            icon={<Pencil size={14} />}
                                                                            onClick={() => {
                                                                                startRenameLocationGroup(
                                                                                    locationMenuKey,
                                                                                    locationName,
                                                                                    groupProjects,
                                                                                    resolvedLocationId
                                                                                );
                                                                            }}
                                                                        >
                                                                            {t("projectPage.rename")}
                                                                        </Menu.Item>
                                                                        <Menu.Item
                                                                            icon={<SlidersHorizontal size={14} />}
                                                                            onClick={async () => {
                                                                                await openLocationAccessModal(resolvedLocationId, locationName, groupProjects);
                                                                            }}
                                                                            disabled={!isAdmin}
                                                                        >
                                                                            {t("projects.manageLocationAccess")}
                                                                        </Menu.Item>
                                                                    </Menu.Content>
                                                                </Menu>
                                                            )}
                                                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <ul className={styles.locationList}>
                                                            {groupProjects.map(project => (
                                                                <li
                                                                    key={project.id}
                                                                    className={styles.projectItem}
                                                                    onClick={() => handleProjectNavigation(project)}
                                                                >
                                                                    <NavLink
                                                                        to={`/app/projects/${project.id}`}
                                                                        className={({ isActive }) => (isActive ? styles.projectLinkActive : styles.projectLink)}
                                                                        onClick={e => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleProjectNavigation(project);
                                                                        }}
                                                                    >
                                                                        <User size={16} className={styles.projectIcon} />
                                                                        <span className={styles.projectName}>
                                                                            <span
                                                                                style={{
                                                                                    overflow: "hidden",
                                                                                    textOverflow: "ellipsis",
                                                                                    whiteSpace: "nowrap"
                                                                                }}
                                                                            >
                                                                                {project.name}
                                                                            </span>
                                                                        </span>
                                                                    </NavLink>
                                                                    {!project.organization_id ? (
                                                                        <Menu>
                                                                            <Menu.Trigger asChild>
                                                                                <button
                                                                                    type="button"
                                                                                    className={styles.menuTrigger}
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    <MoreHorizontal size={16} />
                                                                                </button>
                                                                            </Menu.Trigger>
                                                                            <Menu.Content>
                                                                                <Menu.Item
                                                                                    icon={<Pencil size={14} />}
                                                                                    onClick={() =>
                                                                                        setRenameModal({ isOpen: true, project, value: project.name })
                                                                                    }
                                                                                >
                                                                                    {t("projectPage.rename")}
                                                                                </Menu.Item>
                                                                                <Menu.Item
                                                                                    icon={<MapPin size={14} />}
                                                                                    onClick={() =>
                                                                                        setLocationModal({
                                                                                            isOpen: true,
                                                                                            project,
                                                                                            value: project.location_name || ""
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    {t("projects.location")}
                                                                                </Menu.Item>
                                                                                <Menu.Item
                                                                                    icon={<Users size={14} />}
                                                                                    onClick={() => {
                                                                                        setAccessError(null);
                                                                                        setAccessModal({
                                                                                            isOpen: true,
                                                                                            project,
                                                                                            selectedOids: project.responsible_users?.map(u => u.oid) || []
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    {t("projects.manageAccess")}
                                                                                </Menu.Item>
                                                                                <Menu.Item
                                                                                    icon={<Trash2 size={14} />}
                                                                                    onClick={() => setDeleteModal({ isOpen: true, project })}
                                                                                    danger
                                                                                >
                                                                                    {t("projectPage.delete")}
                                                                                </Menu.Item>
                                                                            </Menu.Content>
                                                                        </Menu>
                                                                    ) : null}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                        })
                                    )}
                                </ul>
                            )}
                        </li>
                    )}

                    {/* Chat History Section */}
                    {((useLogin && showChatHistoryCosmos && sidebarOpen) || (showChatHistoryBrowser && sidebarOpen)) && (
                        <li className={styles.projectsSection}>
                            <div className={styles.projectsHeader} onClick={() => setChatHistoryExpanded(!chatHistoryExpanded)}>
                                <div className={styles.projectsHeaderLeft}>
                                    <History size={18} className={styles.sidebarIcon} />
                                    <span>{t("history.chatHistory")}</span>
                                </div>
                                <div className={styles.projectsHeaderRight}>{chatHistoryExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
                            </div>
                            {chatHistoryExpanded && <HistoryPanel provider={historyProvider} notify={sidebarOpen} showTitle={false} />}
                        </li>
                    )}
                </ul>
            </div>

            {/* Rename Project Modal */}
            <Modal
                open={renameModal.isOpen && !!renameModal.project}
                onOpenChange={open => {
                    if (!open && !isSubmitting) setRenameModal({ isOpen: false, project: null, value: "" });
                }}
                size="sm"
            >
                <Modal.Header
                    title={t("projectPage.rename")}
                    onClose={isSubmitting ? false : () => setRenameModal({ isOpen: false, project: null, value: "" })}
                />
                <form
                    onSubmit={async e => {
                        e.preventDefault();
                        if (!renameModal.project || !renameModal.value.trim() || renameModal.value === renameModal.project.name) return;
                        setIsSubmitting(true);
                        try {
                            await updateProject(renameModal.project.id, { name: renameModal.value.trim() });
                            setRenameModal({ isOpen: false, project: null, value: "" });
                        } catch (error) {
                            console.error("Failed to rename project:", error);
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    <Modal.Body>
                        <FormField label={t("projects.projectName")} htmlFor="rename-project-input">
                            <Input
                                id="rename-project-input"
                                type="text"
                                value={renameModal.value}
                                onChange={e => setRenameModal(prev => ({ ...prev, value: e.target.value }))}
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </FormField>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setRenameModal({ isOpen: false, project: null, value: "" })}
                            disabled={isSubmitting}
                        >
                            {t("labels.cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            disabled={isSubmitting || !renameModal.value.trim() || renameModal.value === renameModal.project?.name}
                            isLoading={isSubmitting}
                        >
                            {t("labels.save")}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Set Location Modal */}
            <Modal
                open={locationModal.isOpen && !!locationModal.project}
                onOpenChange={open => {
                    if (!open && !isSubmitting) setLocationModal({ isOpen: false, project: null, value: "" });
                }}
                size="sm"
            >
                <Modal.Header
                    title={t("projects.location")}
                    onClose={isSubmitting ? false : () => setLocationModal({ isOpen: false, project: null, value: "" })}
                />
                <form
                    onSubmit={async e => {
                        e.preventDefault();
                        if (!locationModal.project) return;
                        setIsSubmitting(true);
                        try {
                            await updateProject(locationModal.project.id, { locationId: null, locationName: locationModal.value.trim() || null });
                            setLocationModal({ isOpen: false, project: null, value: "" });
                        } catch (error) {
                            console.error("Failed to update location:", error);
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    <Modal.Body>
                        <FormField label={t("projects.location")} htmlFor="location-input">
                            <Input
                                id="location-input"
                                type="text"
                                value={locationModal.value}
                                onChange={e => setLocationModal(prev => ({ ...prev, value: e.target.value }))}
                                placeholder={t("projects.selectLocation")}
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </FormField>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setLocationModal({ isOpen: false, project: null, value: "" })}
                            disabled={isSubmitting}
                        >
                            {t("labels.cancel")}
                        </Button>
                        <Button variant="primary" size="sm" type="submit" isLoading={isSubmitting}>
                            {t("labels.save")}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Delete Project Modal */}
            <Modal
                open={deleteModal.isOpen && !!deleteModal.project}
                onOpenChange={open => {
                    if (!open && !isSubmitting) setDeleteModal({ isOpen: false, project: null });
                }}
                size="sm"
            >
                <Modal.Header title={t("projectPage.delete")} onClose={isSubmitting ? false : () => setDeleteModal({ isOpen: false, project: null })} />
                <Modal.Body>
                    <p>
                        {t("projectPage.deleteConfirm")} <strong>"{deleteModal.project?.name}"</strong>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setDeleteModal({ isOpen: false, project: null })} disabled={isSubmitting}>
                        {t("projectPage.cancel")}
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={async () => {
                            if (!deleteModal.project) return;
                            setIsSubmitting(true);
                            try {
                                const projectId = deleteModal.project.id;
                                await deleteProject(projectId);
                                setDeleteModal({ isOpen: false, project: null });
                                if (location.pathname === `/app/projects/${projectId}`) {
                                    navigate("/app");
                                }
                            } catch (error) {
                                console.error("Failed to delete project:", error);
                            } finally {
                                setIsSubmitting(false);
                            }
                        }}
                        isLoading={isSubmitting}
                    >
                        {t("projectPage.delete")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Location Access Modal */}
            <Modal
                open={locationAccessModal.isOpen}
                onOpenChange={open => {
                    if (!open && !isSubmitting) closeLocationAccessModal();
                }}
                size="md"
            >
                <Modal.Header
                    title={t("projects.manageLocationAccessTitle", { location: locationAccessModal.locationName })}
                    onClose={isSubmitting ? false : closeLocationAccessModal}
                />
                <Modal.Body>
                    {locationAccessError ? <Alert variant="error" description={locationAccessError} /> : null}
                    <div className={styles.accessPermissionBanner}>{t("projects.manageLocationAccessHelp")}</div>
                    {locationAccessLoading ? (
                        <p className={styles.accessModalEmpty}>{t("projects.loading")}</p>
                    ) : members.length === 0 ? (
                        <p className={styles.accessModalEmpty}>{t("projects.noMembers")}</p>
                    ) : (
                        <div className={styles.accessUserList}>
                            {members.map(member => (
                                <label key={member.id} className={styles.accessUserItem}>
                                    <input
                                        type="checkbox"
                                        checked={locationAccessModal.selectedUserIds.includes(member.id)}
                                        onChange={() => toggleLocationAccessUser(member.id)}
                                        disabled={isSubmitting}
                                    />
                                    <span className={styles.accessUserName}>{member.display_name}</span>
                                    <span className={styles.accessUserEmail}>{member.email}</span>
                                    {member.role === "admin" && (
                                        <Badge variant="status" tone="accent" size="sm">
                                            {t("projectPage.admin")}
                                        </Badge>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={closeLocationAccessModal} disabled={isSubmitting}>
                        {t("labels.cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveLocationAccess}
                        disabled={isSubmitting || locationAccessLoading || !locationAccessLoaded}
                        isLoading={isSubmitting}
                    >
                        {t("labels.save")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Manage Access Modal */}
            <Modal
                open={accessModal.isOpen && !!accessModal.project}
                onOpenChange={open => {
                    if (!open && !isSubmitting) {
                        setAccessError(null);
                        setAccessModal({ isOpen: false, project: null, selectedOids: [] });
                    }
                }}
                size="md"
            >
                <Modal.Header
                    title={t("projects.manageAccess")}
                    onClose={
                        isSubmitting
                            ? false
                            : () => {
                                  setAccessError(null);
                                  setAccessModal({ isOpen: false, project: null, selectedOids: [] });
                              }
                    }
                />
                <Modal.Body>
                    {accessError ? <Alert variant="error" description={accessError} /> : null}
                    {accessModal.project?.location_id && <div className={styles.accessPermissionBanner}>{t("projects.manageAccessLocationManagedNotice")}</div>}
                    {!isAdmin && !isRoleLoading && <div className={styles.accessPermissionBanner}>{t("projects.manageAccessReadOnly")}</div>}
                    {isRoleLoading ? (
                        <p className={styles.accessModalEmpty}>{t("projects.loading")}</p>
                    ) : roleLoadError ? (
                        <p className={styles.accessModalEmpty}>{t("projects.manageAccessError")}</p>
                    ) : members.length === 0 ? (
                        <p className={styles.accessModalEmpty}>{t("projects.noMembers")}</p>
                    ) : (
                        <div className={styles.accessUserList}>
                            {members.map(member => (
                                <label key={member.id} className={`${styles.accessUserItem} ${!isAdmin ? styles.accessUserItemReadOnly : ""}`}>
                                    <input
                                        type="checkbox"
                                        checked={accessModal.selectedOids.includes(member.id)}
                                        onChange={() => toggleAccessUser(member.id)}
                                        disabled={!isAdmin || isSubmitting}
                                    />
                                    <span className={styles.accessUserName}>{member.display_name}</span>
                                    <span className={styles.accessUserEmail}>{member.email}</span>
                                    {member.role === "admin" && (
                                        <Badge variant="status" tone="accent" size="sm">
                                            {t("projectPage.admin")}
                                        </Badge>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                            setAccessError(null);
                            setAccessModal({ isOpen: false, project: null, selectedOids: [] });
                        }}
                        disabled={isSubmitting}
                    >
                        {t("labels.cancel")}
                    </Button>
                    {isAdmin && (
                        <Button variant="primary" size="sm" onClick={handleSaveAccess} isLoading={isSubmitting}>
                            {t("labels.save")}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            <div className={styles.sidebarLoginWrapper}>{useLogin && <UserProfileMenu sidebarOpen={sidebarOpen} />}</div>
        </nav>
    );
};
